import type {
  DiscountActivityParams,
  DisplayPriceResult,
  PriceCalculationParams,
  Promo,
  PromoResolutionParams,
} from './types';

/**
 * Constant: basis points divisor for discount percentage calculation
 * Deduction amounts are stored as basis points (e.g., 500 = 5%)
 */
const BASIS_POINTS_DIVISOR = 100;

/**
 * Resolves which price to display to the customer
 *
 * Priority order:
 * 1. Compiled promo price (user selected for checkout)
 * 2. Single discount price (if quantity qualified)
 * 3. Base unit price
 *
 * @param params - Price calculation parameters
 * @param params.hasCompiledDiscount - Whether compiled promo is active
 * @param params.compiledPromoPrice - Final price from compiled promo
 * @param params.hasDiscount - Whether single discount exists
 * @param params.isActive - Whether discount is currently active
 * @param params.priceAfterDiscount - Price after single discount applied
 * @param params.unitPrice - Base unit price
 * @returns The price amount to display (not formatted, in cents)
 *
 * @example
 * const displayPrice = resolveDisplayPrice({
 *   hasCompiledDiscount: true,
 *   compiledPromoPrice: 40493,
 *   hasDiscount: true,
 *   isActive: true,
 *   priceAfterDiscount: 40493,
 *   unitPrice: 42624,
 * });
 * // Returns: 40493
 */
export const resolveDisplayPrice = (params: PriceCalculationParams): number => {
  switch (true) {
    case params.hasCompiledDiscount:
      return params.compiledPromoPrice;

    case params.hasDiscount && params.isActive:
      return params.priceAfterDiscount;

    default:
      return params.unitPrice;
  }
};

/**
 * Resolves which promo object to use based on priority
 *
 * Priority order:
 * 1. Compiled promo (from cart store)
 * 2. Bundled discount promo
 * 3. Single discount promo
 * 4. Undefined (no promo)
 *
 * @param params - Promo resolution parameters
 * @param params.hasCompiledDiscount - Whether compiled promo is active
 * @param params.compiledPromo - The compiled promo object
 * @param params.hasBundledDiscount - Whether bundled discount applies
 * @param params.product - Product with discount/bundled promos
 * @returns The resolved promo object or undefined if no promo applies
 * @throws Error if promo arrays are empty when expected to have values
 *
 * @example
 * const promo = resolvePromo({
 *   hasCompiledDiscount: true,
 *   compiledPromo: { id: '123', deductionAmount: 500, ... },
 *   hasBundledDiscount: false,
 *   product: { discountPromos: [...], ... },
 * });
 * // Returns: compiled promo
 */
export const resolvePromo = (
  params: PromoResolutionParams
): Promo | undefined => {
  switch (true) {
    case params.hasCompiledDiscount && !!params.compiledPromo:
      return params.compiledPromo;

    case params.hasBundledDiscount: {
      const bundledPromo = params.product.bundledDiscountPromos?.[0];
      if (!bundledPromo) {
        throw new Error(
          'Bundled discount flag set but no promo found in bundledDiscountPromos array'
        );
      }
      return bundledPromo;
    }

    default: {
      const singlePromo = params.product.discountPromos?.[0];
      if (params.product.discountPromos?.length === 0) {
        throw new Error('Discount expected but discountPromos array is empty');
      }
      return singlePromo;
    }
  }
};

/**
 * Calculates the discount percentage from deduction amount
 *
 * Note: Dividing by BASIS_POINTS_DIVISOR (100) because deduction_amount
 * is stored as basis points (e.g., 500 = 5%, 1000 = 10%)
 *
 * @param deductionAmount - The deduction amount in basis points
 * @returns Discount percentage (e.g., 5 for 5%)
 * @throws Error if deduction amount is negative
 *
 * @example
 * const percentage = calculateDiscountPercentage(500);
 * // Returns: 5 (represents 5% discount)
 *
 * @example
 * const percentage = calculateDiscountPercentage(1000);
 * // Returns: 10 (represents 10% discount)
 */
export const calculateDiscountPercentage = (
  deductionAmount: number
): number => {
  if (deductionAmount < 0) {
    throw new Error('Deduction amount cannot be negative');
  }

  return deductionAmount / BASIS_POINTS_DIVISOR;
};

/**
 * Determines if a discount is currently active (showing to user)
 *
 * Active when:
 * - User has selected this item with compiled promo, OR
 * - Item quantity naturally qualifies for discount minimum
 *
 * Note: Bundled discounts follow different rules and return false
 *
 * @param params - Discount qualification parameters
 * @param params.hasCompiledDiscount - Whether compiled promo is selected
 * @param params.currentQuantity - Current cart quantity
 * @param params.minimumQtyForDiscount - Minimum qty needed for discount
 * @param params.hasBundledDiscount - Whether this is a bundled discount
 * @returns true if discount is active, false otherwise
 *
 * @example
 * const isActive = isDiscountActive({
 *   hasCompiledDiscount: true,
 *   currentQuantity: 6000,
 *   minimumQtyForDiscount: 6000,
 *   hasBundledDiscount: false,
 * });
 * // Returns: true (compiled promo selected)
 *
 * @example
 * const isActive = isDiscountActive({
 *   hasCompiledDiscount: false,
 *   currentQuantity: 5000,
 *   minimumQtyForDiscount: 6000,
 *   hasBundledDiscount: false,
 * });
 * // Returns: false (quantity below minimum)
 */
export const isDiscountActive = (params: DiscountActivityParams): boolean => {
  if (params.hasCompiledDiscount) {
    return true;
  }

  if (params.hasBundledDiscount) {
    return false;
  }

  return params.currentQuantity >= params.minimumQtyForDiscount;
};

/**
 * Creates a display-ready result with all price and discount information
 *
 * @param displayPrice - The final price to display
 * @param originalPrice - The base price without discounts
 * @param discountPercentage - The discount percentage applied
 * @returns DisplayPriceResult with all calculated values
 *
 * @example
 * const result = createDisplayPriceResult(40493, 42624, 5);
 * // Returns:
 * // {
 * //   price: 40493,
 * //   originalPrice: 42624,
 * //   isDiscounted: true,
 * //   discountPercentage: 5,
 * // }
 */
export const createDisplayPriceResult = (
  displayPrice: number,
  originalPrice: number,
  discountPercentage: number
): DisplayPriceResult => {
  return {
    price: displayPrice,
    originalPrice,
    isDiscounted: displayPrice < originalPrice,
    discountPercentage,
  };
};
